import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, MoreHorizontal, Pencil, PhoneCall, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Sheet, SheetContent } from '@/components/atoms/sheet';
import { DataTableDrawerHeader } from '@/components/molecules';
import { DatePicker } from '@/components/molecules/date-picker';
import { TimePicker } from '@/components/molecules/time-picker';
import { SettingsDataGrid, type SettingsDataGridColumn } from '@/components/organisms';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import { useSchedulerTechnicians } from '@/hooks/scheduler/useSchedulerTechnicians';
import {
  dateKeyInTimeZone,
  dateStringToDate,
  timeKeyInTimeZone,
  zonedDateTimeToUtc,
} from '@/lib/date-values';
import {
  useOnCallSchedules,
  useCreateOnCall,
  useUpdateOnCall,
  useDeleteOnCall,
  useEmergencyDispatches,
  type OnCallSchedule,
  type OnCallInput,
} from '@/hooks/scheduler/useOnCall';

export const Route = createFileRoute('/_dashboard/dashboard/settings/oncall')({
  component: OnCallPage,
});

type OnCallColumn = 'technician' | 'period' | 'priority' | 'escalation' | 'status';
type OnCallStatus = 'now' | 'upcoming' | 'past';

const COLUMNS: SettingsDataGridColumn<OnCallColumn>[] = [
  { key: 'technician', label: 'Technician', width: 280, flex: 1 },
  { key: 'period', label: 'Period', width: 320 },
  { key: 'priority', label: 'Priority', align: 'center', width: 140 },
  { key: 'escalation', label: 'Escalation', width: 220 },
  { key: 'status', label: 'Status', align: 'center', width: 150 },
];

const STATUS_ORDER: Record<OnCallStatus, number> = { now: 0, upcoming: 1, past: 2 };

function onCallStatus(row: OnCallSchedule, now: number): OnCallStatus {
  const start = new Date(row.startDate).getTime();
  const end = new Date(row.endDate).getTime();
  if (now < start) return 'upcoming';
  if (now > end) return 'past';
  return 'now';
}

function priorityLabel(priority: number): string {
  return priority <= 0 ? 'Primary' : `Backup · ${priority}`;
}

function formatInstant(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

function OnCallPage() {
  const { data: schedules = [], isLoading } = useOnCallSchedules();
  const { data: emergencyDispatches = [] } = useEmergencyDispatches();
  const { data: businessSettings } = useBusinessSettings();
  const createOnCall = useCreateOnCall();
  const updateOnCall = useUpdateOnCall();
  const deleteOnCall = useDeleteOnCall();

  const timezone = businessSettings?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [drawer, setDrawer] = useState<{
    mode: 'create' | 'edit';
    oncall: OnCallSchedule | null;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OnCallSchedule | null>(null);
  const [search, setSearch] = useState('');

  const now = Date.now();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return schedules;
    return schedules.filter(
      (row) => row.name.toLowerCase().includes(q) || row.phone.toLowerCase().includes(q),
    );
  }, [schedules, search]);

  const onCallNow = useMemo(
    () =>
      schedules
        .filter((row) => onCallStatus(row, now) === 'now')
        .sort((a, b) => a.priority - b.priority),
    [schedules, now],
  );

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const byStatus = STATUS_ORDER[onCallStatus(a, now)] - STATUS_ORDER[onCallStatus(b, now)];
        if (byStatus !== 0) return byStatus;
        return a.priority - b.priority;
      }),
    [filtered, now],
  );

  function statusBadge(status: OnCallStatus) {
    if (status === 'now') {
      return <Badge className="border-0 bg-success/10 text-success">On call now</Badge>;
    }
    if (status === 'upcoming') {
      return <Badge className="border-0 bg-blue-50 text-blue-700">Upcoming</Badge>;
    }
    return <Badge className="border-0 bg-muted text-muted-foreground">Past</Badge>;
  }

  function renderCell(key: OnCallColumn, row: OnCallSchedule) {
    switch (key) {
      case 'technician':
        return (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{row.name}</p>
            <p className="truncate text-xs text-muted-foreground">{row.phone}</p>
          </div>
        );
      case 'period':
        return (
          <span className="text-sm tabular-nums">
            {formatInstant(row.startDate, timezone)} – {formatInstant(row.endDate, timezone)}
          </span>
        );
      case 'priority':
        return <span className="text-sm">{priorityLabel(row.priority)}</span>;
      case 'escalation':
        return (
          <div className="min-w-0 text-sm">
            <p className="truncate">
              {row.supervisorTechnician?.name ?? 'Backup on-duty technician'}
            </p>
            <p className="text-xs text-muted-foreground">
              after {row.acknowledgementTimeoutMinutes} min
            </p>
          </div>
        );
      case 'status':
        return statusBadge(onCallStatus(row, now));
    }
  }

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">On-call</h1>
              <p className="text-xs text-muted-foreground">
                The technician customers reach after hours, with primary and backup rotations.
              </p>
            </div>
            <Button size="sm" onClick={() => setDrawer({ mode: 'create', oncall: null })}>
              <Plus className="h-4 w-4" />
              Add shift
            </Button>
          </>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        {emergencyDispatches.some(
          (dispatch) => dispatch.status === 'PENDING' || dispatch.status === 'ESCALATED',
        ) ? (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" />
              Active emergency dispatches
            </p>
            <div className="space-y-1.5">
              {emergencyDispatches
                .filter(
                  (dispatch) => dispatch.status === 'PENDING' || dispatch.status === 'ESCALATED',
                )
                .slice(0, 5)
                .map((dispatch) => (
                  <div key={dispatch.id} className="flex flex-wrap items-center gap-x-2 text-sm">
                    <span className="font-medium">{dispatch.appointment.serviceType.name}</span>
                    <span className="text-muted-foreground">
                      {dispatch.appointment.customerName} · {dispatch.technician.name}
                    </span>
                    <Badge
                      className={
                        dispatch.status === 'ESCALATED'
                          ? 'border-0 bg-destructive/10 text-destructive'
                          : 'border-0 bg-amber-100 text-amber-800'
                      }
                    >
                      {dispatch.status === 'ESCALATED' ? 'Escalated' : 'Awaiting acknowledgement'}
                    </Badge>
                  </div>
                ))}
            </div>
          </div>
        ) : null}

        {onCallNow.length > 0 ? (
          <div className="border-b border-success/20 bg-success/5 px-4 py-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-success">
              <PhoneCall className="h-3.5 w-3.5" />
              On call now
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              {onCallNow.map((row) => (
                <div key={row.id} className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{row.name}</span>
                  <span className="text-muted-foreground">{row.phone}</span>
                  <span className="text-xs text-muted-foreground">
                    · {priorityLabel(row.priority)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <SettingsDataGrid
          columns={COLUMNS}
          rows={sorted}
          getRowKey={(row) => row.id}
          renderCell={(key, row) => renderCell(key, row)}
          storageKey="settings-oncall"
          emptyIcon={PhoneCall}
          emptyTitle={schedules.length === 0 ? 'No on-call shifts yet' : 'No matching shifts'}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search on-call..."
          showClearAll={Boolean(search)}
          onClearAll={() => setSearch('')}
          activeFilterCount={0}
          showExport={false}
          isLoading={isLoading}
          skeletonRowCount={6}
          renderRowActions={(row) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setDrawer({ mode: 'edit', oncall: row })}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setDeleteTarget(row)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />
      </DetailPageLayout>

      <Sheet open={Boolean(drawer)} onOpenChange={(open) => !open && setDrawer(null)}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
          showCloseButton={false}
        >
          {drawer ? (
            <OnCallFormContent
              mode={drawer.mode}
              oncall={drawer.oncall}
              timezone={timezone}
              isPending={createOnCall.isPending || updateOnCall.isPending}
              onClose={() => setDrawer(null)}
              onCreate={(dto) => createOnCall.mutate(dto, { onSuccess: () => setDrawer(null) })}
              onUpdate={(id, dto) =>
                updateOnCall.mutate({ id, dto }, { onSuccess: () => setDrawer(null) })
              }
            />
          ) : null}
        </SheetContent>
      </Sheet>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete on-call shift?</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? `Remove ${deleteTarget.name}'s on-call shift. This can't be undone.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteOnCall.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteOnCall.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function splitInstant(iso: string, timezone: string): { date: Date; time: string } {
  const date = dateStringToDate(dateKeyInTimeZone(iso, timezone)) ?? new Date(iso);
  return { date, time: timeKeyInTimeZone(iso, timezone) };
}

function combineInstant(date: Date | undefined, time: string, timezone: string): Date | null {
  return date ? (zonedDateTimeToUtc(date, time, timezone) ?? null) : null;
}

function OnCallFormContent({
  mode,
  oncall,
  timezone,
  isPending,
  onClose,
  onCreate,
  onUpdate,
}: {
  mode: 'create' | 'edit';
  oncall: OnCallSchedule | null;
  timezone: string;
  isPending: boolean;
  onClose: () => void;
  onCreate: (dto: OnCallInput) => void;
  onUpdate: (id: string, dto: Partial<OnCallInput>) => void;
}) {
  const { data: technicians = [] } = useSchedulerTechnicians();
  const isEdit = mode === 'edit';

  const seededStart = oncall ? splitInstant(oncall.startDate, timezone) : null;
  const seededEnd = oncall ? splitInstant(oncall.endDate, timezone) : null;

  const [technicianId, setTechnicianId] = useState(oncall?.technicianId ?? '');
  const [supervisorTechnicianId, setSupervisorTechnicianId] = useState(
    oncall?.supervisorTechnicianId ?? 'none',
  );
  const [phone, setPhone] = useState(oncall?.phone ?? '');
  const [startDate, setStartDate] = useState<Date | undefined>(seededStart?.date);
  const [startTime, setStartTime] = useState(seededStart?.time ?? '09:00');
  const [endDate, setEndDate] = useState<Date | undefined>(seededEnd?.date);
  const [endTime, setEndTime] = useState(seededEnd?.time ?? '17:00');
  const [priority, setPriority] = useState(String(oncall?.priority ?? 0));
  const [acknowledgementTimeoutMinutes, setAcknowledgementTimeoutMinutes] = useState(
    String(oncall?.acknowledgementTimeoutMinutes ?? 15),
  );
  const [notes, setNotes] = useState(oncall?.notes ?? '');

  function handleTechnician(id: string) {
    setTechnicianId(id);
    if (supervisorTechnicianId === id) setSupervisorTechnicianId('none');
    // Prefill the phone from the technician (editable afterwards).
    const tech = technicians.find((t) => t.id === id);
    if (tech?.phone) setPhone(tech.phone);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const tech = technicians.find((t) => t.id === technicianId);
    const start = combineInstant(startDate, startTime, timezone);
    const end = combineInstant(endDate, endTime, timezone);
    if (!technicianId || !tech) {
      toast.error('Select a technician');
      return;
    }
    if (!start || !end) {
      toast.error('Start and end are required');
      return;
    }
    if (end <= start) {
      toast.error('End must be after start');
      return;
    }
    const dto: OnCallInput = {
      technicianId,
      supervisorTechnicianId: supervisorTechnicianId === 'none' ? null : supervisorTechnicianId,
      name: tech.name,
      phone: phone.trim() || tech.phone || '',
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      priority: Math.max(0, Number(priority) || 0),
      acknowledgementTimeoutMinutes: Math.min(
        1440,
        Math.max(1, Number(acknowledgementTimeoutMinutes) || 15),
      ),
      notes: notes.trim() || null,
    };
    if (isEdit && oncall) onUpdate(oncall.id, dto);
    else onCreate(dto);
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <DataTableDrawerHeader
        title={isEdit ? 'Edit on-call shift' : 'Add on-call shift'}
        onClose={onClose}
      />
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <div className="space-y-1.5">
          <Label htmlFor="oncall-tech">Technician</Label>
          <Select value={technicianId} onValueChange={handleTechnician} disabled={isPending}>
            <SelectTrigger id="oncall-tech" className="w-full">
              <SelectValue placeholder="Select a technician" />
            </SelectTrigger>
            <SelectContent>
              {technicians.map((tech) => (
                <SelectItem key={tech.id} value={tech.id}>
                  {tech.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="oncall-phone">Phone</Label>
          <Input
            id="oncall-phone"
            value={phone}
            disabled={isPending}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 555 0100"
          />
          <p className="text-xs text-muted-foreground">Defaults from the technician — editable.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="oncall-supervisor">Escalation supervisor</Label>
          <Select
            value={supervisorTechnicianId}
            onValueChange={setSupervisorTechnicianId}
            disabled={isPending}
          >
            <SelectTrigger id="oncall-supervisor" className="w-full">
              <SelectValue placeholder="Select a supervisor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Use the next on-duty backup</SelectItem>
              {technicians
                .filter((tech) => tech.id !== technicianId)
                .map((tech) => (
                  <SelectItem key={tech.id} value={tech.id}>
                    {tech.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Receives SMS and email if this technician does not acknowledge.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Start</Label>
          <div className="flex items-center gap-2">
            <DatePicker value={startDate} onChange={setStartDate} className="w-44" />
            <TimePicker value={startTime} onChange={setStartTime} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>End</Label>
          <div className="flex items-center gap-2">
            <DatePicker value={endDate} onChange={setEndDate} className="w-44" />
            <TimePicker value={endTime} onChange={setEndTime} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="oncall-priority">Priority</Label>
          <Input
            id="oncall-priority"
            type="number"
            min={0}
            value={priority}
            disabled={isPending}
            onChange={(e) => setPriority(e.target.value)}
            className="w-28"
          />
          <p className="text-xs text-muted-foreground">0 = first to call; 1, 2… = backups.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="oncall-ack-timeout">Acknowledgement timeout</Label>
          <div className="flex items-center gap-2">
            <Input
              id="oncall-ack-timeout"
              type="number"
              min={1}
              max={1440}
              value={acknowledgementTimeoutMinutes}
              disabled={isPending}
              onChange={(e) => setAcknowledgementTimeoutMinutes(e.target.value)}
              className="w-28"
            />
            <span className="text-sm text-muted-foreground">minutes</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="oncall-notes">Notes</Label>
          <Textarea
            id="oncall-notes"
            value={notes}
            disabled={isPending}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
          />
        </div>
      </div>
      <div className="flex flex-row justify-end gap-2 border-t px-5 py-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
